import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';

export interface PillSwitcherOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export type PillSwitcherVariant = 'default' | 'brand';

@Component({
  selector: 'sg-pill-switcher',
  templateUrl: './sg-pill-switcher.component.html',
  styleUrls: ['./sg-pill-switcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SgPillSwitcherComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() options: PillSwitcherOption[] = [];
  @Input() value: string = '';
  @Input() variant: PillSwitcherVariant = 'default';
  @Input() equalWidth: boolean = false;
  /** Horizontal padding for each segment button. Accepts any CSS value (e.g. '24px', 'clamp(12px, 2vw, 24px)'). Default: 24px (via CSS). */
  @Input() padding: string = '';
  @Input() ariaLabel: string = '';
  @Output() valueChange = new EventEmitter<string>();

  @ViewChildren('segmentBtn') segmentButtons: QueryList<ElementRef<HTMLButtonElement>>;
  @ViewChild('indicator') indicator: ElementRef<HTMLDivElement>;

  singleSelectable = false;
  private initialized = false;
  private pendingRafIds: number[] = [];
  private resizeObserver: ResizeObserver | null = null;

  constructor(private hostRef: ElementRef) {}

  ngAfterViewInit(): void {
    this.initialized = true;
    this.updateIndicator(false);
    this.observeResize();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.singleSelectable = this.options.filter((o) => !o.disabled).length <= 1;
    }

    if ((changes['value'] || changes['options']) && this.initialized) {
      this.scheduleRaf(() => this.updateIndicator(true));
    }
  }

  ngOnDestroy(): void {
    this.pendingRafIds.forEach(cancelAnimationFrame);
    this.resizeObserver?.disconnect();
  }

  select(option: PillSwitcherOption): void {
    if (option.disabled || option.value === this.value) return;
    this.valueChange.emit(option.value);
  }

  private scheduleRaf(callback: () => void): void {
    this.pendingRafIds.push(requestAnimationFrame(callback));
  }

  private observeResize(): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.scheduleRaf(() => this.updateIndicator(false));
    });
    this.resizeObserver.observe(this.hostRef.nativeElement);
  }

  private updateIndicator(animate: boolean): void {
    const buttons = this.segmentButtons?.toArray();
    const el = this.indicator?.nativeElement;
    if (!buttons?.length || !el) return;

    const activeIndex = this.options.findIndex((o) => o.value === this.value);
    if (activeIndex === -1) {
      el.style.width = '0';

      return;
    }

    const btn = buttons[activeIndex]?.nativeElement;
    if (!btn) return;

    if (!animate) {
      el.style.transition = 'none';
    }

    el.style.left = `${btn.offsetLeft}px`;
    el.style.width = `${btn.offsetWidth}px`;

    if (!animate) {
      // Force reflow so the browser commits `transition: none` before restoring the transition
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      btn.offsetHeight;
      el.style.transition = '';
    }
  }
}
